import { Deserializable } from '../../protocols/deserializable';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { UnitOfMeasure } from '../../enum/dto/unit-of-measure.enum';
import { CannabisUnitOfMeasure } from '../../enum/dto/cannabis-unit-of-measure.enum';
import { ProductType } from '../../enum/dto/product-type.enum';
import { VariantType } from '../../enum/dto/variant-type.enum';
import { VariantInventory } from './variant-inventory';
import { VariantPricing } from './variant-pricing';
import { DisplayAttribute } from '../../display/dto/display-attribute';
import { Cachable } from '../../protocols/cachable';
import { DateUtils } from '../../../utils/date-utils';
import { CompanyConfiguration } from '../../company/dto/company-configuration';
import { Menu } from '../../menu/menu';
import { VariantBadge } from './variant-badge';
import { UniquelyIdentifiable } from '../../protocols/uniquely-identifiable';
import { LocationPromotion } from './location-promotion';
import { MenuType } from '../../enum/dto/menu-type.enum';
import { CannabinoidDisplayType } from '../../enum/shared/cannabinoid-display-type.enum';
import { NumberUtils } from '../../../utils/number.utils';
import { LocationPriceStream } from '../../enum/shared/location-price-stream';
import { LocationConfiguration } from '../../company/dto/location-configuration';
import { PriceUtils } from '../../../utils/price-utils';
import { SectionLayoutType } from '../../enum/dto/section-layout-type.enum';
import { VariantPricingTier } from './variant-pricing-tier';
import { Label } from '../../menu/labels/label';
import { StringUtils } from '../../../utils/string-utils';
import { VariantTypeUtils } from '../../../utils/variant-type-utils';
import { TerpeneUnitOfMeasure } from '../../enum/dto/terpene-unit-of-measure';
import { TerpeneDisplayType } from '../../enum/shared/terpene-display-type';
import { exists } from '../../../functions/exists';
import { memoize } from '../../../modules/shared/decorators/memoize';
import { Terpene } from '../../enum/shared/terpene';
import { GradingResult } from './grading-result';
import { CraftDesignation } from '../../enum/dto/craft-designation';
import { Cannabinoid, PrimaryCannabinoid, SecondaryCannabinoid } from '../../enum/shared/cannabinoid';
import { GroupDetailEnergyScore } from '../../enum/dto/group-detail-energy-score';
import { RegexUtils } from '../../../utils/regex-utils';
import { SortUtils } from '../../../utils/sort-utils';
import { cacheResult } from '../../../modules/shared/decorators/cache-result';

export class Variant implements Deserializable, Cachable, UniquelyIdentifiable {
  static buildArrayCacheKey(companyId, locationId: number, productId: string): string {
    return `Variants-${companyId}-${locationId}-${productId}`;
  }

  static buildCacheKey(companyId, locationId: number, variantId: string): string {
    return `Variant-${companyId}-${locationId}-${variantId}`;
  }

  // DTO
  public companyId: number;
  public id: string;
  public catalogItemId: string;
  public catalogSKU: string;
  public barcode: string;
  public name: string;
  public price: number;
  public lastModified: number;
  public brand: string;
  public lineages: string[];
  public manufacturer: string;
  public description: string;
  public shortDescription: string;
  public richTextDescription: string;
  public terpenes: string;
  public classification: StrainType;
  public unitSize: number;
  public unitOfMeasure: UnitOfMeasure;
  public cannabisUnitOfMeasure: CannabisUnitOfMeasure;
  public terpeneUnitOfMeasure: TerpeneUnitOfMeasure;
  public packagedQuantity: number;
  public productType: ProductType;
  public variantType: VariantType;
  public strain: string;
  public minTHC: string;
  public maxTHC: string;
  public THC: string;
  public minCBD: string;
  public maxCBD: string;
  public CBD: string;
  public inventory: VariantInventory;
  public locationPricing: VariantPricing[];
  public inventoryCount: number;
  public displayAttributes: DisplayAttribute;
  public locationPromotion: LocationPromotion;
  public isDiscontinued: boolean;
  public dateCreated: number;
  // These custom properties aren't always set. They are conditionally set based on the company, POS, etc.
  public custom1: string;
  public custom2: string;
  public custom3: string;
  // Advanced Properties
  public gradingResults: Map<string, GradingResult>;
  public energyScore: GroupDetailEnergyScore;
  public craftDesignations: CraftDesignation[];
  public flavorGroup: string;
  public helpsWith: string[];
  public effects: string[];
  public aromas: string[];
  public flavors: string[];
  public appearances: string[];
  public growingConditions: string[];
  public tagIds: string[];
  public posTagIds: string[];

  /**
   * Below information only pertains to the [Display], and not [Dashboard] or [Admin]
   *
   * This property gets consolidated from 2 into 1 before the data is sent back to the display app.
   * The property variant.useCannabinoidRange gets ||'d with companyConfiguration.cannabinoidDisplayType === 'range'.
   * Therefore, this signal is simplified to a single boolean at the variant level, so that the display app
   * doesn't need context to the company configuration everywhere in order to know if the variant is using
   * ranged cannabinoids.
   *
   * So the data is transformed like this on the backend:
   * this.useCannabinoidRange = variant.useCannabinoidRange || companyConfiguration.cannabinoidDisplayType === 'range';
   */
  public useCannabinoidRange: boolean;

  /**
   * Below information only pertains to the [Display], and not [Dashboard] or [Admin]
   *
   * This property gets consolidated from 2 into 1 before the data is sent back to the display app.
   * The property variant.useTerpeneRange gets ||'d with companyConfiguration.terpeneDisplayType === 'range'.
   * Therefore, this signal is simplified to a single boolean at the variant level, so that the display app
   * doesn't need context to the company configuration everywhere in order to know if the variant is using
   * ranged terpenes.
   *
   * So the data is transformed like this on the backend:
   * this.useTerpeneRange = variant.useTerpeneRange || companyConfiguration.terpeneDisplayType === 'range';
   */
  public useTerpeneRange: boolean;

  // Cache
  public cachedTime: number;
  // Parent Product
  public productId: string;
  public productName: string;
  // Unique Identifier
  public uniqueIdentifier: string = '';
  // Not set from api.
  public enabledSecondaryCannabinoids: SecondaryCannabinoid[];
  // Not set from api.
  // Set by sativa/indica/hybrid section on themes supporting Auto-Balance column overflow (Plantlife)
  moveToCenterColumn: boolean = false;

  public onDeserialize() {
    this.inventory = window?.injector?.Deserialize?.instanceOf(VariantInventory, this.inventory);
    this.locationPricing = window?.injector?.Deserialize?.arrayOf(VariantPricing, this.locationPricing);
    this.displayAttributes = window?.injector?.Deserialize?.instanceOf(DisplayAttribute, this.displayAttributes);
    this.locationPromotion = window?.injector?.Deserialize?.instanceOf(LocationPromotion, this.locationPromotion);
    this.gradingResults = window?.injector?.Deserialize?.mapOf(GradingResult, this.gradingResults) || new Map();
    this.lineages = Array.from(this.lineages || []).sort(SortUtils.stringAscendingNullsLast);
    this.tagIds = Array.from(this.tagIds || []);
    this.posTagIds = Array.from(this.posTagIds || []);
    this.helpsWith = Array.from(this.helpsWith || []);
    this.effects = Array.from(this.effects || []);
    this.appearances = Array.from(this.appearances || []);
    this.aromas = Array.from(this.aromas || []);
    this.growingConditions = Array.from(this.growingConditions || []);
    this.craftDesignations = Array.from(this.craftDesignations || []);
    this.flavors = Array.from(this.flavors || []);
  }

  cacheExpirySeconds(): number {
    return DateUtils.unixOneDay();
  }

  cacheKey(companyId, locationId: number): string {
    return Variant.buildCacheKey(companyId, locationId, this.id);
  }

  isExpired(): boolean {
    const expiresAt = this.cachedTime + this.cacheExpirySeconds();
    return DateUtils.nowInUnixSeconds() > expiresAt;
  }

  inStock(): boolean {
    return this.inventory?.inStock();
  }

  @memoize
  protected getCompanyDisplayAttribute(): DisplayAttribute | null {
    return (
      this.getDisplayAttributes()
        ?.filter(da => da?.isCompanyDA())
        ?.firstOrNull() || null
    );
  }

  @memoize
  protected getLocationDisplayAttribute(): DisplayAttribute | null {
    return (
      this.getDisplayAttributes()
        ?.filter(da => da?.isLocationDA())
        ?.firstOrNull() || null
    );
  }

  withinGridColumn(columnName: string): boolean {
    const unitForComparison = StringUtils.gridColumnComparisonString(this.getGridName());
    const comparingTo = StringUtils.gridColumnComparisonString(columnName);
    return unitForComparison === comparingTo;
  }

  withinGridColumnAndInStock(columnName: string, showOutOfStock: boolean): boolean {
    return this.withinGridColumn(columnName) && (showOutOfStock || this.inStock());
  }

  @memoize
  isSaleLabelActive(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream
  ): boolean {
    return this.hasDiscount(themeId, locationId, companyId, priceStream);
  }

  @cacheResult
  getLineagesAsString(): string {
    return this.lineages?.join(' x ') || '';
  }

  @cacheResult
  getEffectsAsString(): string {
    return this.effects?.join(' x ') || '';
  }

  @cacheResult
  getAromasAsString(): string {
    return this.aromas?.join(' x ') || '';
  }

  @cacheResult
  getFlavorsAsString(): string {
    return this.flavors?.join(' x ') || '';
  }

  @cacheResult
  getHelpsWithAsString(): string {
    return this.helpsWith?.join(' x ') || '';
  }

  @cacheResult
  getAppearancesAsString(): string {
    return this.appearances?.join(' x ') || '';
  }

  @cacheResult
  getGrowingConditionsAsString(): string {
    return this.growingConditions?.join(' x ') || '';
  }

  @cacheResult
  getCraftDesignationsAsString(): string {
    return this.craftDesignations?.join(' x ') || '';
  }

  isRestockLabelActive(restockLabel: Label): boolean {
    const currentTimestamp = DateUtils.nowInUnixSeconds();
    const threshold = restockLabel?.timeThreshold * DateUtils.unixOneHour();
    const validRestockUntil = this.inventory?.lastThresholdRestock + threshold;
    const validRestockThreshold = NumberUtils.floatFirstGreaterThanSecond(this.inventory?.lastThresholdRestock, 0);
    const isStocked = NumberUtils.floatFirstGreaterThanSecond(this.inventory?.quantityInStock, 0);
    const withinRestockThreshold = currentTimestamp < validRestockUntil;
    return validRestockThreshold && isStocked && withinRestockThreshold;
  }

  isLowStockLabelActive(lowStockLabel: Label): boolean {
    const quantityInStock = this.inventory?.quantityInStock;
    const threshold = lowStockLabel?.numericThreshold;
    return (
      NumberUtils.floatFirstGreaterThanSecond(quantityInStock, 0) &&
      NumberUtils.floatFirstGreaterThanSecond(threshold, quantityInStock)
    );
  }

  isNewLabelActive(newLabel: Label): boolean {
    const threshold = (this.inventory?.firstInventory ?? 0) + (newLabel?.timeThreshold ?? 0) * DateUtils.unixOneDay();
    return NumberUtils.floatFirstGreaterThanSecond(threshold, DateUtils.nowInUnixSeconds());
  }

  /* ************************************* Pricing ************************************* */

  protected roundFinalPrice(price: number): number | null {
    return Number.isFinite(price) ? PriceUtils.roundPrice(price) : null;
  }

  /**
   * Location SALE > Company SALE > Location Price > Company Price
   *
   * If no companyId is provided, this method will use the companyId attached to the variant.
   *
   * @returns the current sticker price of the variant, ie the price that a customer would
   * pay if they were to buy the variant at the store.
   */
  @memoize
  getVisiblePrice(
    themeId: string,
    locId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    hideSale: boolean,
    sectionLayoutType: SectionLayoutType = null,
    columnTitle: string = null
  ): number | null {
    if (sectionLayoutType === SectionLayoutType.PricingTierGrid) {
      return this.getVisiblePriceForTier(themeId, locId, companyId, priceStream, hideSale, columnTitle);
    } else if (sectionLayoutType === SectionLayoutType.ClassicFlowerGrid) {
      return this.getVisiblePriceForClassicFlowerWeight(themeId, locId, companyId, priceStream, hideSale, columnTitle);
    }
    const company = this.locationPricing?.find(p => p.locationId === (companyId || this.companyId));
    const location = this.locationPricing?.find(p => p.locationId === locId);
    const locPromo = this.locationPromotion;
    const returnNull = (): null => null;
    type DiscountPriceInterface = (
      themeId: string,
      companyId: number,
      locationId: number,
      locationPriceStream: LocationPriceStream,
      locationPrice: LocationPromotion
    ) => number;
    const locationBestDiscountedPriceOrNull: DiscountPriceInterface = !hideSale
      ? (location?.getBestDiscountedPriceOrNull?.bind(location) ?? returnNull)
      : returnNull;
    const companyBestDiscountedPriceOrNull: DiscountPriceInterface = !hideSale
      ? (company?.getBestDiscountedPriceOrNull?.bind(company) ?? returnNull)
      : returnNull;
    const calculatePromotionOnOriginalPriceOrNull = (): number => {
      return (
        locPromo
          ?.applyPromotionDiscountOrNull(this.price)
          ?.applyPriceStreamRounding(themeId, companyId, locId, priceStream) || null
      );
    };
    const promotionOnOriginalPriceOrNull: () => number = !hideSale
      ? calculatePromotionOnOriginalPriceOrNull
      : returnNull;
    const visiblePrice =
      locationBestDiscountedPriceOrNull?.(themeId, companyId, locId, priceStream, locPromo) ||
      location?.getPriceWithoutDiscountsOrNull(themeId, companyId, locId, priceStream) ||
      companyBestDiscountedPriceOrNull?.(themeId, companyId, locId, priceStream, locPromo) ||
      company?.getPriceWithoutDiscountsOrNull(themeId, companyId, locId, priceStream) ||
      promotionOnOriginalPriceOrNull() ||
      this.price?.applyPriceStreamRounding(themeId, companyId, locId, priceStream) ||
      null;
    return this.roundFinalPrice(visiblePrice);
  }

  /**
   * Don't apply price stream rounding if you're trying to calculate a discounted price. Do that after the discount
   * has been applied.
   */
  protected getPriceForRegularPricingTier(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    columnTitle: string,
    applyPriceStreamRounding: boolean = true
  ): number {
    const pricingTier = this.getPricingTierForGridName(companyId, locationId, columnTitle);
    switch (priceStream) {
      case LocationPriceStream.TaxesIn:
      case LocationPriceStream.TaxesInRounded: {
        if (!applyPriceStreamRounding) return pricingTier?.getTaxesInPrice();
        return pricingTier?.getTaxesInPrice()?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
      }
      default: {
        if (!applyPriceStreamRounding) return pricingTier?.getRegularPrice();
        return pricingTier?.getRegularPrice()?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
      }
    }
  }

  protected getDiscountedPriceForRegularPricingTierOrNull(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    columnTitle: string
  ): number | null {
    if (this.locationPromotion?.isValidForPricingTier()) {
      const price = this.getPriceForRegularPricingTier(themeId, locationId, companyId, priceStream, columnTitle, false);
      return this.locationPromotion
        ?.applyPromotionDiscountOrNull(price)
        ?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
    }
    return null;
  }

  protected getVisiblePriceForTier(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    hideSale: boolean,
    columnTitle: string = null
  ): number {
    let priceForTier;
    if (hideSale) {
      priceForTier = this.getPriceForRegularPricingTier(themeId, locationId, companyId, priceStream, columnTitle);
    } else {
      priceForTier =
        this.getDiscountedPriceForRegularPricingTierOrNull(themeId, locationId, companyId, priceStream, columnTitle) ||
        this.getPriceForRegularPricingTier(themeId, locationId, companyId, priceStream, columnTitle);
    }
    return this.roundFinalPrice(priceForTier);
  }

  protected getPriceForClassicFlowerPricingTier(
    themeId: string,
    companyId: number,
    locationId: number,
    columnTitle: string,
    priceStream: LocationPriceStream,
    applyPriceStreamRounding: boolean = true
  ): number {
    const price = this.getClassicFlowerPricingTierFromGridName(
      companyId,
      locationId,
      columnTitle
    )?.classicFlowerPricing(columnTitle, priceStream);
    if (!applyPriceStreamRounding) return price;
    return price?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
  }

  protected getDiscountedPriceForClassicFlowerPricingTier(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    columnTitle: string
  ): number | null {
    if (this.locationPromotion?.isValidForClassicFlowerGrid()) {
      const price = this.getPriceForClassicFlowerPricingTier(
        themeId,
        locationId,
        companyId,
        columnTitle,
        priceStream,
        false
      );
      return this.locationPromotion
        ?.applyPromotionDiscountOrNull(price)
        ?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
    }
    return null;
  }

  protected getVisiblePriceForClassicFlowerWeight(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream,
    hideSale: boolean,
    columnTitle: string
  ): number {
    let priceForWeight;
    if (hideSale) {
      priceForWeight = this.getPriceForClassicFlowerPricingTier(
        themeId,
        companyId,
        locationId,
        columnTitle,
        priceStream
      );
    } else {
      priceForWeight =
        this.getDiscountedPriceForClassicFlowerPricingTier(themeId, locationId, companyId, priceStream, columnTitle) ||
        this.getPriceForClassicFlowerPricingTier(themeId, companyId, locationId, columnTitle, priceStream);
    }
    return this.roundFinalPrice(priceForWeight);
  }

  /**
   * If no companyId is provided, this method will use the companyId attached to the variant.
   * Location Price > Company Price > Regular Price > null
   *
   * I did not break this up into separate variables because I want the logic to be calculated
   * via a short-circuit to reduce the amount of computation being done.
   *
   * @returns the price of the variant without promotions or discounts applied.
   */
  @memoize
  getPriceWithoutDiscounts(
    tId: string, // themeId
    lId: number, // locationId
    compId: number | null,
    priceStream: LocationPriceStream
  ): number {
    const prices = this.locationPricing;
    const cId = compId || this.companyId;
    return (
      prices?.find(p => p.locationId === lId)?.getPriceWithoutDiscountsOrNull(tId, cId, lId, priceStream) ||
      prices?.find(p => p.locationId === cId)?.getPriceWithoutDiscountsOrNull(tId, cId, lId, priceStream) ||
      this.price?.applyPriceStreamRounding(tId, cId, lId, priceStream) ||
      null
    );
  }

  /**
   * Location Best Discount > Company Best Discount > Regular Price with Discount? > null
   * If no companyId is provided, this method will use the companyId attached to the variant.
   *
   * I did not break this up into separate variables because I want the logic to be calculated
   * via a short-circuit to reduce the amount of computation being done.
   *
   * @returns the sale or promotion price of the variant, else null.
   */
  @memoize
  getDiscountedPriceOrNull(
    tId: string, // themeId
    lId: number, // locationId
    compId: number | null,
    priceStream: LocationPriceStream
  ): number | null {
    const prices = this.locationPricing;
    const promo = this.locationPromotion;
    const cId = compId || this.companyId;
    const locationPrice = prices?.find(p => p.locationId === lId);
    if (locationPrice?.getVisiblePrice(tId, cId, lId, priceStream, promo) > 0) {
      return locationPrice.getBestDiscountedPriceOrNull(tId, cId, lId, priceStream, promo) || null;
    }
    return (
      prices?.find(p => p.locationId === cId)?.getBestDiscountedPriceOrNull(tId, cId, lId, priceStream, promo) ||
      promo?.applyPromotionDiscountOrNull(this.price)?.applyPriceStreamRounding(tId, cId, lId, priceStream) ||
      null
    );
  }

  /**
   * If no companyId is provided, this method will use the companyId attached to the variant.
   *
   * @returns true if the variant is discounted, else false.
   */
  @memoize
  hasDiscount(
    themeId: string,
    locationId: number,
    companyId: number | null,
    priceStream: LocationPriceStream
  ): boolean {
    return this.getDiscountedPriceOrNull(themeId, locationId, companyId, priceStream) !== null;
  }

  @memoize
  getSecondaryPrice(themeId: string, companyId: number, locationId: number, priceSteam: LocationPriceStream): number {
    companyId = Number(companyId ? companyId : this.companyId);
    locationId = Number(locationId);
    const company = this.locationPricing?.find(p => p.locationId === (companyId || this.companyId));
    const location = this.locationPricing?.find(p => p.locationId === locationId);
    let priceVal: number;
    if (location?.secondaryPrice > 0) {
      priceVal = location.secondaryPrice;
    } else if (company?.secondaryPrice > 0) {
      priceVal = company.secondaryPrice;
    }
    // Don't pass in the price stream. Price stream is only relevant for primary price.
    return priceVal?.applyPriceStreamRounding(themeId, companyId, locationId, null);
  }

  @memoize
  getPricePerUOM(
    themeId: string,
    locationId: number,
    companyId: number,
    priceStream: LocationPriceStream,
    hideSale: boolean
  ): number {
    companyId = Number(companyId ? companyId : this.companyId);
    locationId = Number(locationId);
    const numerator = this.getVisiblePrice(themeId, locationId, companyId, priceStream, hideSale);
    const denominator = (this.packagedQuantity ?? 1) * (this.unitSize ?? 1);
    return isFinite(denominator) && denominator !== 0
      ? PriceUtils.roundToNearestHundredthOrNull(numerator / denominator)
      : 0;
  }

  @memoize
  getTaxesInPrice(themeId: string, locationId: number, companyId: number): number {
    return this.getVisiblePrice(themeId, locationId, companyId, LocationPriceStream.TaxesIn, false);
  }

  @memoize
  getTaxesInRoundedPrice(themeId: string, locationId: number, companyId: number): number {
    return this.getVisiblePrice(themeId, locationId, companyId, LocationPriceStream.TaxesInRounded, false);
  }

  @memoize
  getPreTaxPrice(themeId: string, locationId: number, companyId: number): number {
    return this.getVisiblePrice(themeId, locationId, companyId, LocationPriceStream.Default, false);
  }

  /**
   * Location SALE > Company SALE > Location Price > Company Price
   *
   * @returns [price, priceText, isSale]
   */
  @memoize
  getFormattedPrice(
    themeId: string,
    compId: number,
    locId: number,
    priceStream: LocationPriceStream,
    ignoreSalePrice: boolean = false,
    hidePriceDecimal: boolean = false,
    locName?: string,
    compName?: string
  ): [string, string, boolean] {
    const location = this.locationPricing?.find(p => p.locationId === locId);
    const company = this.locationPricing?.find(p => p.locationId === this.companyId);
    const locPromo = this.locationPromotion;
    let priceVal: number;
    let priceText: string;
    let isSale = false;
    const buildPrice = (): [string, string, boolean] => {
      return [PriceUtils.formatPrice(priceVal, hidePriceDecimal), priceText, isSale];
    };
    // location discount
    priceVal = location?.getBestDiscountedPriceOrNull(themeId, compId, locId, priceStream, locPromo);
    priceText = locName || 'location';
    isSale = true;
    if (priceVal > 0 && !ignoreSalePrice) {
      const isPromo = priceVal === location?.getPromotionPriceOrNull(themeId, compId, locId, priceStream, locPromo);
      priceText = isPromo ? 'promotion price' : priceText + ' sale price';
      return buildPrice();
    }
    // company discount
    priceVal = company?.getBestDiscountedPriceOrNull(themeId, compId, locId, priceStream, locPromo);
    priceText = compName || 'company';
    isSale = true;
    if (priceVal > 0 && !ignoreSalePrice) {
      const isPromo = priceVal === company?.getPromotionPriceOrNull(themeId, compId, locId, priceStream, locPromo);
      priceText = isPromo ? 'promotion price' : priceText + ' sale price';
      return buildPrice();
    }
    // location pricing
    priceVal = location?.getPriceWithoutDiscountsOrNull(themeId, compId, locId, priceStream);
    priceText = locName || 'location price';
    isSale = false;
    if (priceVal > 0) return buildPrice();
    // company pricing
    priceVal = company?.getPriceWithoutDiscountsOrNull(themeId, compId, locId, priceStream);
    priceText = compName || 'company price';
    isSale = false;
    if (priceVal > 0) return buildPrice();
    // default price
    priceVal = this.price?.applyPriceStreamRounding(themeId, compId, locId, priceStream);
    priceText = 'default price';
    isSale = false;
    return buildPrice();
  }

  @memoize
  getBasePriceOrNull(
    themeId: string,
    companyId: number,
    locationId: number,
    priceStream: LocationPriceStream
  ): number | null {
    const base = this.price?.applyPriceStreamRounding(themeId, companyId, locationId, priceStream);
    return Number.isFinite(base) ? PriceUtils.roundPrice(base) : null;
  }

  @memoize
  getCompanyPriceWithoutDiscountsOrNull(
    themeId: string,
    companyId: number,
    locationId: number,
    priceStream: LocationPriceStream
  ): number | null {
    const company = this.locationPricing?.find(p => p.locationId === this.companyId);
    const companyPrice = company?.getPriceWithoutDiscountsOrNull(themeId, companyId, locationId, priceStream) || null;
    return Number.isFinite(companyPrice) ? PriceUtils.roundPrice(companyPrice) : null;
  }

  @memoize
  getCompanySecondaryPriceOrNull(): number | null {
    const company = this.locationPricing?.find(p => p.locationId === this.companyId);
    const companySecondaryPrice = company?.secondaryPrice || null;
    return Number.isFinite(companySecondaryPrice) ? PriceUtils.roundPrice(companySecondaryPrice) : null;
  }

  @memoize
  getLocationPriceWithoutDiscountsOrNull(
    themeId: string,
    companyId: number,
    locationId: number,
    priceStream: LocationPriceStream
  ): number | null {
    const location = this.locationPricing?.find(p => p.locationId === locationId);
    const locationPrice = location?.getPriceWithoutDiscountsOrNull(themeId, companyId, locationId, priceStream) || null;
    return Number.isFinite(locationPrice) ? PriceUtils.roundPrice(locationPrice) : null;
  }

  @memoize
  getLocationSecondaryPriceOrNull(locationId: number): number | null {
    const location = this.locationPricing?.find(p => p.locationId === locationId);
    const locationSecondaryPrice = location?.secondaryPrice || null;
    return Number.isFinite(locationSecondaryPrice) ? PriceUtils.roundPrice(locationSecondaryPrice) : null;
  }

  @memoize
  onSale(
    themeId: string,
    locationId: number,
    ignoreSalePrice: boolean = false,
    priceStream: LocationPriceStream
  ): boolean {
    if (ignoreSalePrice) return false;
    return this.getDiscountedPriceOrNull(themeId, locationId, this.companyId, priceStream) !== null;
  }

  /**
   * The original price on the variant is conditionally shown if a sale, discount or promotion is
   * applied to the variant. If no sale, discount or promotion is applied, then no value is shown in the column.
   */
  @memoize
  getSaleOriginalPriceOrNull(
    themeId: string,
    locationId: number,
    companyId: number,
    priceStream: LocationPriceStream
  ): number | null {
    return this.onSale(themeId, locationId, false, priceStream)
      ? this.getVisiblePrice(themeId, locationId, companyId, priceStream, true)
      : null;
  }

  /* *************************** Cannabinoids and Terpenes *************************** */

  protected getTotalActiveCannabinoids(
    enabledSecondaryCannabinoid: SecondaryCannabinoid[],
    rangeAccessor?: 'min' | 'max'
  ): number {
    const locationDA = this.getLocationDisplayAttribute();
    const locationOverride = locationDA?.[rangeAccessor ? `${rangeAccessor}TAC` : 'TAC'];
    if (exists(locationOverride)) {
      return Number(locationOverride);
    }
    let locationLevelCannabinoidsSet: string[];
    if (rangeAccessor) {
      locationLevelCannabinoidsSet =
        rangeAccessor === 'min'
          ? this.getLocationMinSpecificCannabinoids(locationDA, enabledSecondaryCannabinoid)
          : this.getLocationMaxSpecificCannabinoids(locationDA, enabledSecondaryCannabinoid);
    } else {
      locationLevelCannabinoidsSet = this.getLocationSpecificCannabinoids(
        false,
        locationDA,
        enabledSecondaryCannabinoid
      );
    }
    if (locationLevelCannabinoidsSet?.length > 0) {
      return this.getAutoCalculatedTotalActiveCannabinoids(enabledSecondaryCannabinoid, rangeAccessor);
    }
    const companyDA = this.getCompanyDisplayAttribute();
    const companyOverride = companyDA?.[rangeAccessor ? `${rangeAccessor}TAC` : 'TAC'];
    if (exists(companyOverride)) {
      return Number(companyOverride);
    }
    return this.getAutoCalculatedTotalActiveCannabinoids(enabledSecondaryCannabinoid, rangeAccessor);
  }

  @memoize
  protected getLocationMinSpecificCannabinoids(
    locationDA: DisplayAttribute,
    enabledSecondaryCannabinoid: SecondaryCannabinoid[]
  ): string[] {
    return this.getEnabledCannabinoids(enabledSecondaryCannabinoid)?.filter(cannabinoid => {
      return this.hasLocationSpecificCannabinoidSet(`min${cannabinoid}`, locationDA);
    });
  }

  @memoize
  protected getLocationMaxSpecificCannabinoids(
    locationDA: DisplayAttribute,
    enabledSecondaryCannabinoid: SecondaryCannabinoid[]
  ): string[] {
    return this.getEnabledCannabinoids(enabledSecondaryCannabinoid)?.filter(cannabinoid => {
      return this.hasLocationSpecificCannabinoidSet(`max${cannabinoid}`, locationDA);
    });
  }

  @memoize
  protected getLocationSpecificCannabinoids(
    useRange: boolean,
    locationDisplayAttribute: DisplayAttribute,
    enabledSecondaryCannabinoid: SecondaryCannabinoid[],
    ignoredCannabinoids?: string[]
  ): string[] {
    const enabledCannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoid, ignoredCannabinoids);
    return enabledCannabinoids.filter(cannabinoid => {
      return useRange
        ? this.hasLocationSpecificRangedCannabinoidSet(cannabinoid, locationDisplayAttribute)
        : this.hasLocationSpecificCannabinoidSet(cannabinoid, locationDisplayAttribute);
    });
  }

  /**
   * 0 is valid, because 0 can be an explicitly set value.
   */
  @memoize
  protected hasLocationSpecificRangedCannabinoidSet(
    cannabinoid: string,
    locationDisplayAttribute: DisplayAttribute
  ): boolean {
    return (
      this.hasLocationSpecificCannabinoidSet(`min${cannabinoid}`, locationDisplayAttribute) ||
      this.hasLocationSpecificCannabinoidSet(`max${cannabinoid}`, locationDisplayAttribute)
    );
  }

  /**
   * 0 is valid, because 0 can be an explicitly set value.
   */
  @memoize
  protected hasLocationSpecificCannabinoidSet(
    cannabinoid: string,
    locationDisplayAttribute: DisplayAttribute
  ): boolean {
    return exists(locationDisplayAttribute?.[cannabinoid]) && parseFloat(locationDisplayAttribute?.[cannabinoid]) >= 0;
  }

  @memoize
  getNumericTotalActiveCannabinoids(enabledSecondaryCannabinoid: SecondaryCannabinoid[]): number {
    return this.getTotalActiveCannabinoids(enabledSecondaryCannabinoid);
  }

  @memoize
  getNumericMinTotalActiveCannabinoids(enabledSecondaryCannabinoid: SecondaryCannabinoid[]): number {
    return this.getTotalActiveCannabinoids(enabledSecondaryCannabinoid, 'min');
  }

  @memoize
  getNumericMaxTotalActiveCannabinoids(enabledSecondaryCannabinoid: SecondaryCannabinoid[]): number {
    return this.getTotalActiveCannabinoids(enabledSecondaryCannabinoid, 'max');
  }

  @memoize
  protected getAutoCalculatedTotalActiveCannabinoids(
    enabledSecondaryCannabinoid: SecondaryCannabinoid[],
    rangeAccessor?: 'min' | 'max'
  ): number {
    const cannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoid);
    const prefix = rangeAccessor ?? '';
    const sources: (DisplayAttribute | Variant)[] = [...this.getDisplayAttributes(), this];
    const count = (total: number, cannabinoid: string): number => {
      const value = sources?.reduce((acc, curr) => acc || curr?.[`${prefix}${cannabinoid}`], null);
      const valueAsNumber = exists(value) ? parseFloat(value) : 0;
      if (!valueAsNumber) return total;
      if (!Number.isFinite(total)) total = 0;
      return total + valueAsNumber;
    };
    return cannabinoids.reduce(count, 0);
  }

  @memoize
  private getTotalActiveTerpenes(enabledTerpenesPascalCased: string[], rangeAccessor?: 'min' | 'max'): number {
    const locationDA = this.getLocationDisplayAttribute();
    const locationOverride = locationDA?.[rangeAccessor ? `${rangeAccessor}TotalTerpene` : 'totalTerpene'];
    if (exists(locationOverride)) {
      return Number(locationOverride);
    }
    let locationLevelTerpenesSet: string[];
    if (rangeAccessor) {
      locationLevelTerpenesSet =
        rangeAccessor === 'min'
          ? this.getLocationMinSpecificTerpenes(locationDA, enabledTerpenesPascalCased)
          : this.getLocationMaxSpecificTerpenes(locationDA, enabledTerpenesPascalCased);
    } else {
      locationLevelTerpenesSet = this.getLocationSpecificTerpenes(false, locationDA, enabledTerpenesPascalCased);
    }
    if (locationLevelTerpenesSet?.length > 0) {
      return this.getAutoCalculatedTotalActiveTerpenes(enabledTerpenesPascalCased, rangeAccessor);
    }
    const companyDA = this.getCompanyDisplayAttribute();
    const companyOverride = companyDA?.[rangeAccessor ? `${rangeAccessor}TotalTerpene` : 'totalTerpene'];
    if (exists(companyOverride)) {
      return Number(companyOverride);
    }
    return this.getAutoCalculatedTotalActiveTerpenes(enabledTerpenesPascalCased, rangeAccessor);
  }

  @memoize
  getNumericTotalActiveTerpenes(enabledTerpenesPascalCased: string[]): number {
    return this.getTotalActiveTerpenes(enabledTerpenesPascalCased);
  }

  @memoize
  getNumericMinTotalActiveTerpenes(enabledTerpenesPascalCased: string[]): number {
    return this.getTotalActiveTerpenes(enabledTerpenesPascalCased, 'min');
  }

  @memoize
  getNumericMaxTotalActiveTerpenes(enabledTerpenesPascalCased: string[]): number {
    return this.getTotalActiveTerpenes(enabledTerpenesPascalCased, 'max');
  }

  @memoize
  protected getAutoCalculatedTotalActiveTerpenes(
    enabledTerpenesPascalCased: string[],
    rangeAccessor?: 'min' | 'max'
  ): number {
    const prefix = rangeAccessor ?? '';
    const sources: (DisplayAttribute | Variant)[] = [...this.getDisplayAttributes(), this];
    const count = (total: number, terpene: string): number => {
      const accessor = exists(prefix) ? `${prefix}${terpene}` : StringUtils.camelize(terpene);
      const value = sources?.reduce((acc, curr) => acc || curr?.[accessor], null);
      const valueAsNumber = exists(value) ? parseFloat(value) : 0;
      if (!valueAsNumber) return total;
      if (!Number.isFinite(total)) total = 0;
      return total + valueAsNumber;
    };
    return enabledTerpenesPascalCased?.reduce(count, undefined);
  }

  @memoize
  getNumericCannabinoidOrTerpene(cannabinoidOrTerpeneCamelCased: string): number {
    const str =
      this.displayAttributes?.[cannabinoidOrTerpeneCamelCased] ||
      this.displayAttributes?.inheritedDisplayAttribute?.[cannabinoidOrTerpeneCamelCased] ||
      this[cannabinoidOrTerpeneCamelCased] ||
      null;
    return this.getCannabinoidOrTerpeneNumericValueFromString(str);
  }

  @memoize
  getNumericMinCannabinoidOrTerpene(cannabinoidOrTerpeneCamelCased: string): number {
    const accessor = StringUtils.capitalize(cannabinoidOrTerpeneCamelCased);
    const displayMinValue =
      this.displayAttributes?.[`min${accessor}`] !== '' ? this.displayAttributes?.[`min${accessor}`] : null;
    const displayInheritedMinValue =
      this.displayAttributes?.inheritedDisplayAttribute?.[`min${accessor}`] !== ''
        ? this.displayAttributes?.inheritedDisplayAttribute?.[`min${accessor}`]
        : null;
    const minValue = this[`min${accessor}`] !== '' ? this[`min${accessor}`] : null;
    const str = displayMinValue ?? displayInheritedMinValue ?? minValue;
    return this.getCannabinoidOrTerpeneNumericValueFromString(str);
  }

  @memoize
  getNumericMaxCannabinoidOrTerpene(cannabinoidOrTerpeneCamelCased: string): number {
    const accessor = StringUtils.capitalize(cannabinoidOrTerpeneCamelCased);
    const displayMinValue =
      this.displayAttributes?.[`max${accessor}`] !== '' ? this.displayAttributes?.[`max${accessor}`] : null;
    const displayInheritedMinValue =
      this.displayAttributes?.inheritedDisplayAttribute?.[`max${accessor}`] !== ''
        ? this.displayAttributes?.inheritedDisplayAttribute?.[`max${accessor}`]
        : null;
    const minValue = this[`max${accessor}`] !== '' ? this[`max${accessor}`] : null;
    const str = displayMinValue ?? displayInheritedMinValue ?? minValue;
    return this.getCannabinoidOrTerpeneNumericValueFromString(str);
  }

  @memoize
  getTopCannabinoid(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): string | null {
    if (this.productType === ProductType.Accessories) {
      return null;
    }
    const locDA = this.displayAttributes;
    const companyDA = this.displayAttributes?.inheritedDisplayAttribute;
    const autoCalculate = () => {
      return this.displayAttributes?.getAutoCalculatedTopCannabinoid(
        this.useCannabinoidRange,
        enabledSecondaryCannabinoids,
        ignoredListCannabinoids
      );
    };
    const locationOverride = this.getOverrideForTopCannabinoid(
      locDA,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
    if (exists(locationOverride)) {
      return locationOverride;
    }
    const locationCannabinoidDataIsSet =
      this.getLocationSpecificCannabinoids(
        this.useCannabinoidRange,
        locDA,
        enabledSecondaryCannabinoids,
        ignoredListCannabinoids
      )?.length > 0;
    if (locationCannabinoidDataIsSet) {
      return autoCalculate();
    }
    const companyOverride = this.getOverrideForTopCannabinoid(
      companyDA,
      enabledSecondaryCannabinoids,
      ignoredListCannabinoids
    );
    if (exists(companyOverride)) {
      return companyOverride;
    }
    return autoCalculate();
  }

  @memoize
  getTopTerpene(enabledTerpenesPascalCased: string[]): string | null {
    if (this.productType === ProductType.Accessories) {
      return null;
    }
    const locDA = this.displayAttributes;
    const companyDA = this.displayAttributes?.inheritedDisplayAttribute;
    const enabledTerpenes = enabledTerpenesPascalCased?.map(terp => StringUtils.splitPascalCasePreserveAcronyms(terp));
    const autoCalculate = () => {
      return this.displayAttributes?.getAutoCalculatedTopTerpene(this.useTerpeneRange, enabledTerpenesPascalCased);
    };
    const locationOverride = this.getOverrideForTopTerpene(locDA, enabledTerpenes);
    if (exists(locationOverride)) {
      return locationOverride;
    }
    const locationTerpeneDataIsSet =
      this.getLocationSpecificTerpenes(this.useTerpeneRange, locDA, enabledTerpenesPascalCased)?.length > 0;
    if (locationTerpeneDataIsSet) {
      return autoCalculate();
    }
    const companyOverride = this.getOverrideForTopTerpene(companyDA, enabledTerpenes);
    if (exists(companyOverride)) {
      return companyOverride;
    }
    return autoCalculate();
  }

  private getOverrideForTopTerpene(displayAttribute: DisplayAttribute, enabledTerpenes: string[]): string | null {
    return enabledTerpenes?.includes(displayAttribute?.topTerpene) ? displayAttribute?.topTerpene : null;
  }

  private getOverrideForTopCannabinoid(
    displayAttribute: DisplayAttribute,
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredListCannabinoids: Cannabinoid[]
  ): string | null {
    const cannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoids, ignoredListCannabinoids);
    return cannabinoids?.includes(displayAttribute?.topCannabinoid) ? displayAttribute?.topCannabinoid : null;
  }

  @memoize
  getPresentTerpenes(enabledTerpenesPascalCased: string[]): Terpene[] {
    const presentTerpenes =
      this.getLocationOverridePresentTerpenesOrNull(enabledTerpenesPascalCased) ||
      this.getPresentTerpenesIfUserHasLocationLevelTerpenesSetOrNull(enabledTerpenesPascalCased) ||
      this.getCompanyOverridePresentTerpenesOrNull(enabledTerpenesPascalCased) ||
      this.getAutoCalculatedPresentTerpenes(enabledTerpenesPascalCased);
    return this.addTopTerpeneToPresentTerpenes(presentTerpenes, this.getTopTerpene(enabledTerpenesPascalCased));
  }

  /**
   * The top terpene always counts as present, whether present terpenes are set by hand or calculated from terpene
   * values, since a top terpene can be set on a terpene that has no value. Names are compared in PascalCase, the
   * way the present terpene overrides are matched.
   */
  private addTopTerpeneToPresentTerpenes(presentTerpenes: Terpene[], topTerpene: string | null): Terpene[] {
    if (!topTerpene) return presentTerpenes;
    const topTerpenePascalCased = StringUtils.toPascalCase(topTerpene);
    if (presentTerpenes?.some(terpene => StringUtils.toPascalCase(terpene) === topTerpenePascalCased)) {
      return presentTerpenes;
    }
    return [...(presentTerpenes ?? []), topTerpene as Terpene];
  }

  @memoize
  private getEnabledCannabinoids(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredCannabinoids?: string[]
  ): Cannabinoid[] {
    const allCannabinoids: Cannabinoid[] = [
      PrimaryCannabinoid.THC,
      PrimaryCannabinoid.CBD,
      ...(enabledSecondaryCannabinoids ?? [])
    ];
    return exists(ignoredCannabinoids)
      ? allCannabinoids.filter(cannabinoid => !ignoredCannabinoids.includes(cannabinoid))
      : allCannabinoids;
  }

  @memoize
  getPresentCannabinoids(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignored?: Cannabinoid[]
  ): Cannabinoid[] | null {
    return (
      this.getLocationOverridePresentCannabinoidsOrNull(enabledSecondaryCannabinoids, ignored) ||
      this.getPresentCannabinoidsIfUserHasLocationLevelCannabinoidsSetOrNull(enabledSecondaryCannabinoids, ignored) ||
      this.getCompanyOverridePresentCannabinoidsOrNull(enabledSecondaryCannabinoids, ignored) ||
      this.getAutoCalculatedPresentCannabinoids(this.getEnabledCannabinoids(enabledSecondaryCannabinoids, ignored)) ||
      null
    );
  }

  protected getLocationOverridePresentTerpenesOrNull(enabledTerpenesPascalCased: string[]): Terpene[] | null {
    const locationDisplayAttribute = this.getLocationDisplayAttribute();
    const enabledLocationPresentTerpenes =
      locationDisplayAttribute?.presentTerpenes?.filter(presentTerpene => {
        const terpPascalCased = StringUtils.toPascalCase(presentTerpene);
        return enabledTerpenesPascalCased?.includes(terpPascalCased);
      }) || null;
    if (enabledLocationPresentTerpenes?.length > 0) {
      return enabledLocationPresentTerpenes;
    }
    return null;
  }

  protected getLocationOverridePresentCannabinoidsOrNull(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredCannabinoids?: Cannabinoid[]
  ): Cannabinoid[] | null {
    const enabledCannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoids, ignoredCannabinoids);
    const locationDisplayAttribute = this.getLocationDisplayAttribute();
    const enabledLocationPresentCannabinoids =
      locationDisplayAttribute?.presentCannabinoids
        ?.filter(presentCannabinoid => enabledCannabinoids?.includes(presentCannabinoid))
        ?.emptyAsNull() || null;
    return enabledLocationPresentCannabinoids?.length > 0 ? enabledLocationPresentCannabinoids : null;
  }

  protected getPresentTerpenesIfUserHasLocationLevelTerpenesSetOrNull(
    enabledTerpenesPascalCased: string[]
  ): Terpene[] | null {
    const locationSpecificTerpenes = this.getLocationSpecificTerpenes(
      this.useTerpeneRange,
      this.getLocationDisplayAttribute(),
      enabledTerpenesPascalCased
    );
    if (locationSpecificTerpenes?.length > 0) {
      return this.getAutoCalculatedPresentTerpenes(enabledTerpenesPascalCased);
    }
    return null;
  }

  protected getPresentCannabinoidsIfUserHasLocationLevelCannabinoidsSetOrNull(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredCannabinoids?: string[]
  ): Cannabinoid[] | null {
    const locationSpecificCannabinoids = this.getLocationSpecificCannabinoids(
      this.useCannabinoidRange,
      this.getLocationDisplayAttribute(),
      enabledSecondaryCannabinoids,
      ignoredCannabinoids
    );
    if (locationSpecificCannabinoids?.length > 0) {
      // location values only gate the auto-calc; presence is still evaluated over every enabled cannabinoid
      const enabledCannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoids, ignoredCannabinoids);
      return this.getAutoCalculatedPresentCannabinoids(enabledCannabinoids);
    }
    return null;
  }

  @memoize
  protected getLocationSpecificTerpenes(
    useRange: boolean,
    locationDisplayAttribute: DisplayAttribute,
    enabledTerpenesPascalCased: string[]
  ): string[] {
    return enabledTerpenesPascalCased
      ?.filter(terpenePascalCased => {
        return useRange
          ? this.hasLocationSpecificRangedTerpeneSet(terpenePascalCased, locationDisplayAttribute)
          : this.hasLocationSpecificTerpeneSet(
              StringUtils.pascalCaseToCamelCase(terpenePascalCased),
              locationDisplayAttribute
            );
      })
      ?.map(terpenePascalCased => StringUtils.splitPascalCasePreserveAcronyms(terpenePascalCased));
  }

  @memoize
  protected getLocationMinSpecificTerpenes(
    locationDA: DisplayAttribute,
    enabledTerpenesPascalCased: string[]
  ): string[] {
    return enabledTerpenesPascalCased
      ?.filter(terpenePascalCased => this.hasLocationSpecificTerpeneSet(`min${terpenePascalCased}`, locationDA))
      ?.map(terpenePascalCased => StringUtils.splitPascalCasePreserveAcronyms(terpenePascalCased));
  }

  @memoize
  protected getLocationMaxSpecificTerpenes(
    locationDA: DisplayAttribute,
    enabledTerpenesPascalCased: string[]
  ): string[] {
    return enabledTerpenesPascalCased
      ?.filter(terpenePascalCased => this.hasLocationSpecificTerpeneSet(`max${terpenePascalCased}`, locationDA))
      ?.map(terpenePascalCased => StringUtils.splitPascalCasePreserveAcronyms(terpenePascalCased));
  }

  /**
   * 0 is valid, because 0 can be an explicitly set value.
   */
  @memoize
  protected hasLocationSpecificRangedTerpeneSet(
    terpenePascalCased: string,
    locationDisplayAttribute: DisplayAttribute
  ): boolean {
    return (
      this.hasLocationSpecificCannabinoidSet(`min${terpenePascalCased}`, locationDisplayAttribute) ||
      this.hasLocationSpecificCannabinoidSet(`max${terpenePascalCased}`, locationDisplayAttribute)
    );
  }

  /**
   * 0 is valid, because 0 can be an explicitly set value.
   */
  @memoize
  protected hasLocationSpecificTerpeneSet(
    terpeneCamelCased: string,
    locationDisplayAttribute: DisplayAttribute
  ): boolean {
    return (
      exists(locationDisplayAttribute?.[terpeneCamelCased]) &&
      parseFloat(locationDisplayAttribute?.[terpeneCamelCased]) >= 0
    );
  }

  @memoize
  protected getCompanyOverridePresentCannabinoidsOrNull(
    enabledSecondaryCannabinoids: SecondaryCannabinoid[],
    ignoredCannabinoids?: string[]
  ): Cannabinoid[] | null {
    const enabledCannabinoids = this.getEnabledCannabinoids(enabledSecondaryCannabinoids, ignoredCannabinoids);
    const companyDisplayAttribute = this.getCompanyDisplayAttribute();
    const enabledCompanyPresentCannabinoids =
      companyDisplayAttribute?.presentCannabinoids?.filter(presentCannabinoid => {
        return enabledCannabinoids?.includes(presentCannabinoid);
      }) || null;
    if (enabledCompanyPresentCannabinoids?.length > 0) {
      return enabledCompanyPresentCannabinoids;
    }
    return null;
  }

  protected getCompanyOverridePresentTerpenesOrNull(enabledTerpenesPascalCased: string[]): Terpene[] | null {
    const companyDisplayAttribute = this.getCompanyDisplayAttribute();
    const enabledCompanyPresentTerpenes =
      companyDisplayAttribute?.presentTerpenes?.filter(presentTerpene => {
        const terpPascalCased = StringUtils.toPascalCase(presentTerpene);
        return enabledTerpenesPascalCased?.includes(terpPascalCased);
      }) || null;
    if (enabledCompanyPresentTerpenes?.length > 0) {
      return enabledCompanyPresentTerpenes;
    }
    return null;
  }

  protected getAutoCalculatedPresentCannabinoids(enabledCannabinoids: string[]): Cannabinoid[] | null {
    return this.displayAttributes?.getAutoCalculatedPresentCannabinoids(
      this,
      this.useCannabinoidRange,
      enabledCannabinoids
    );
  }

  protected getAutoCalculatedPresentTerpenes(enabledTerpenesPascalCased: string[]): Terpene[] | null {
    return this.displayAttributes?.getAutoCalculatedPresentTerpenes(this.useTerpeneRange, enabledTerpenesPascalCased);
  }

  /**
   * if number is negative, returns 0.
   * if v doesn't exist, returns -1.
   */
  getCannabinoidOrTerpeneNumericValueFromString(v: string): number {
    if (exists(v)) {
      if (v.includes('-')) {
        v = v.split('-')[0];
      }
      return Number(v.replace(/[^0-9.]+/g, ''));
    } else {
      return -1;
    }
  }

  @memoize
  getFormattedCannabinoidRange(cannabinoidCamelCased: string): string {
    if (this.cannabisUnitOfMeasure === CannabisUnitOfMeasure.NA) {
      return '--';
    }
    const minVal = this.getNumericMinCannabinoidOrTerpene(cannabinoidCamelCased);
    const maxVal = this.getNumericMaxCannabinoidOrTerpene(cannabinoidCamelCased);
    if (minVal !== -1 && maxVal !== -1) {
      if (minVal === maxVal) {
        return `${minVal}`;
      }
      return `${minVal} - ${maxVal}`;
    }
    return '';
  }

  @memoize
  getFormattedTerpeneRange(terpeneCamelCased: string): string {
    if (this.terpeneUnitOfMeasure === TerpeneUnitOfMeasure.NA) {
      return '--';
    }
    const minVal = this.getNumericMinCannabinoidOrTerpene(terpeneCamelCased);
    const maxVal = this.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased);
    if (minVal !== -1 && maxVal !== -1) {
      if (minVal === maxVal) {
        return `${minVal}`;
      }
      return `${minVal} - ${maxVal}`;
    }
    return '';
  }

  @memoize
  getFormattedCannabinoidWithUnits(menu: Menu, cannabinoidCamelCased: string): string {
    const minVal = this.getNumericMinCannabinoidOrTerpene(cannabinoidCamelCased);
    const maxVal = this.getNumericMaxCannabinoidOrTerpene(cannabinoidCamelCased);
    let rangeWithUnits: string = '';
    if (minVal !== -1 && maxVal !== -1) {
      if (minVal === maxVal) {
        rangeWithUnits = `${minVal}${this.getCannabinoidUnitOfMeasureString(menu)}`;
      }
      const min = `${minVal}${this.getCannabinoidUnitOfMeasureString(menu)}`;
      const max = `${maxVal}${this.getCannabinoidUnitOfMeasureString(menu)}`;
      rangeWithUnits = `${min} - ${max}`;
    }
    return rangeWithUnits || 'N/A';
  }

  @memoize
  getFormattedTerpeneWithUnits(menu: Menu, terpeneCamelCased: string): string {
    const minVal = this.getNumericMinCannabinoidOrTerpene(terpeneCamelCased);
    const maxVal = this.getNumericMaxCannabinoidOrTerpene(terpeneCamelCased);
    let rangeWithUnits: string = '';
    if (minVal !== -1 && maxVal !== -1) {
      if (minVal === maxVal) {
        rangeWithUnits = `${minVal}${this.getTerpeneUnitOfMeasureString(menu)}`;
      }
      const min = `${minVal}${this.getTerpeneUnitOfMeasureString(menu)}`;
      const max = `${maxVal}${this.getTerpeneUnitOfMeasureString(menu)}`;
      rangeWithUnits = `${min} - ${max}`;
    }
    return rangeWithUnits || 'N/A';
  }

  shouldDisplayCannabinoidValue(): boolean {
    return this.productType !== ProductType.Accessories;
  }

  shouldDisplayTerpeneValue(): boolean {
    return this.productType !== ProductType.Accessories;
  }

  @memoize
  getCannabinoidUnitOfMeasureString(menu: Menu): string {
    let unitOfMeasure = this.cannabisUnitOfMeasure;
    if (unitOfMeasure === CannabisUnitOfMeasure.NA) {
      return '-';
    } else if (unitOfMeasure === CannabisUnitOfMeasure.UNKNOWN) {
      if (this.productType === ProductType.Flower) {
        unitOfMeasure = CannabisUnitOfMeasure.Percent;
      } else {
        unitOfMeasure = CannabisUnitOfMeasure.MilliGram;
      }
    }
    let unitOfMeasureString = unitOfMeasure?.toString();
    const isMilliGramPerGram = unitOfMeasure === CannabisUnitOfMeasure.MilliGramPerGram;
    const isMilliGramPerMilliLitre = unitOfMeasure === CannabisUnitOfMeasure.MilliGramPerMilliLitre;
    if ((isMilliGramPerGram || isMilliGramPerMilliLitre) && menu.type !== MenuType.PrintMenu) {
      unitOfMeasureString = `${unitOfMeasure}`;
    }
    return unitOfMeasureString;
  }

  @memoize
  getTerpeneUnitOfMeasureString(menu: Menu): string {
    let unitOfMeasure = this.terpeneUnitOfMeasure;
    if (unitOfMeasure === TerpeneUnitOfMeasure.NA) {
      return '-';
    } else if (unitOfMeasure === TerpeneUnitOfMeasure.UNKNOWN) {
      if (this.productType === ProductType.Flower) {
        unitOfMeasure = TerpeneUnitOfMeasure.Percent;
      } else {
        unitOfMeasure = TerpeneUnitOfMeasure.MilliGram;
      }
    }
    let unitOfMeasureString = unitOfMeasure?.toString();
    const isMilliGramPerGram = unitOfMeasure === TerpeneUnitOfMeasure.MilliGramPerGram;
    const isMilliGramPerMilliLitre = unitOfMeasure === TerpeneUnitOfMeasure.MilliGramPerMilliLitre;
    if ((isMilliGramPerGram || isMilliGramPerMilliLitre) && menu.type !== MenuType.PrintMenu) {
      unitOfMeasureString = `${unitOfMeasure}`;
    }
    return unitOfMeasureString;
  }

  @memoize
  public getCannabinoid(menu: Menu, companyConfig: CompanyConfiguration, cannabinoidCamelCased: string): string {
    const shouldDisplayCannabinoid = this.shouldDisplayCannabinoidValue();
    if (shouldDisplayCannabinoid) {
      const unitOfMeasureString = this.getCannabinoidUnitOfMeasureString(menu);
      const companyUsesCannabinoidRange = companyConfig?.cannabinoidDisplayType === CannabinoidDisplayType.Range;
      const displayCannabinoidInRanges = companyUsesCannabinoidRange || this.useCannabinoidRange;
      if (displayCannabinoidInRanges) {
        const cannabinoidRange = this.getFormattedCannabinoidRange(cannabinoidCamelCased);
        if (cannabinoidRange === '') {
          return `${CannabisUnitOfMeasure.NA}`;
        } else {
          return `${cannabinoidRange} ${unitOfMeasureString}`.trim();
        }
      } else {
        const parsedCannabinoid = this.getNumericCannabinoidOrTerpene(cannabinoidCamelCased);
        if (parsedCannabinoid < 1) {
          return `<1${unitOfMeasureString}`.trim();
        } else {
          return `${Math.round((parsedCannabinoid + Number.EPSILON) * 100) / 100}${unitOfMeasureString}`.trim();
        }
      }
    } else {
      return '-';
    }
  }

  @memoize
  public getTerpene(menu: Menu, companyConfig: CompanyConfiguration, terpeneCamelCased: string): string {
    const shouldDisplayTerpene = this.shouldDisplayTerpeneValue();
    if (shouldDisplayTerpene) {
      const unitOfMeasureString = this.getTerpeneUnitOfMeasureString(menu);
      const companyUsesTerpeneRange = companyConfig?.terpeneDisplayType === TerpeneDisplayType.Range;
      const displayTerpeneInRanges = companyUsesTerpeneRange || this.useTerpeneRange;
      if (displayTerpeneInRanges) {
        const terpeneRange = this.getFormattedTerpeneRange(terpeneCamelCased);
        if (terpeneRange === '') {
          return `${TerpeneUnitOfMeasure.NA}`;
        } else {
          return `${terpeneRange} ${unitOfMeasureString}`.trim();
        }
      } else {
        const parsedTerpene = this.getNumericCannabinoidOrTerpene(terpeneCamelCased);
        if (parsedTerpene < 1) {
          return `<1${unitOfMeasureString}`.trim();
        } else {
          return `${Math.round((parsedTerpene + Number.EPSILON) * 100) / 100}${unitOfMeasureString}`.trim();
        }
      }
    } else {
      return '-';
    }
  }

  /**
   * Cannabinoids are stored as strings, therefore, we need to parse their numerical values,
   * round them, and then replace the old string number with the new rounded string number.
   */
  roundCannabinoids(shouldRound: boolean): void {
    if (!shouldRound) return;
    const cannabinoidProperties = DisplayAttribute.cannabinoidProperties();
    const roundCannabinoid = prop => {
      const cannabinoidString = this[prop]?.match(/(\d+\.?\d*)/)?.[0];
      const roundedCannabinoidNumber = Math.round(parseFloat(this[prop]));
      if (Number.isFinite(roundedCannabinoidNumber)) {
        const rounded = roundedCannabinoidNumber?.toString(10);
        this[prop] = (this[prop] as string)?.replace(cannabinoidString, rounded);
      }
    };
    cannabinoidProperties?.forEach(roundCannabinoid);
    this.displayAttributes?.roundCannabinoids(shouldRound);
  }

  /* ******************************************************************************** */

  @memoize
  getFlavors(): string[] {
    return this.flavors;
  }

  @memoize
  getNumericSize(): number {
    // If variant is non-pre-roll flower
    const isFlowerByGram = VariantTypeUtils.isFlowerByGramType(this.variantType);
    // If variant is any of the concentrate types
    const isConcentrate = this.productType === ProductType.Concentrate;
    // If variant is any of the liquid oil types (non-capsule)
    const isLiquidOil = this.productType === ProductType.Oil && !VariantTypeUtils.isCapsuleType(this.variantType);
    // If variant is any Vape option
    const isVape = this.productType === ProductType.Vape;
    switch (true) {
      case isFlowerByGram:
      case isConcentrate:
      case isLiquidOil:
      case isVape:
        return this.unitSize;
      default:
        return this.packagedQuantity;
    }
  }

  @memoize
  getGridNames(layoutType: SectionLayoutType, locationId: number): string[] | null {
    switch (layoutType) {
      case SectionLayoutType.PricingTierGrid:
      case SectionLayoutType.ClassicFlowerGrid:
        return this.getPricingTierGridNames(this.companyId, locationId, layoutType);
      case SectionLayoutType.Grid:
        return [this.getGridName()];
      default:
        return null;
    }
  }

  @memoize
  private getPricingTierGridNames(
    companyId: number,
    locationId: number,
    layoutType: SectionLayoutType
  ): string[] | null {
    const enabled = this.shouldUseWeightForPricingTierGridColumn();
    const getGridNames = (price: VariantPricing) => {
      const getColumnNames = (pricingTier: VariantPricingTier): string[] | null => {
        switch (layoutType) {
          case SectionLayoutType.PricingTierGrid:
            return [pricingTier?.getGridColumnName(enabled, this.unitOfMeasure)];
          case SectionLayoutType.ClassicFlowerGrid:
            return pricingTier?.getFlowerGridOunceColumnNames(enabled);
        }
      };
      const gridNames = new Set(price?.pricingTiers?.flatMap(getColumnNames)?.filter(exists) ?? []);
      return gridNames?.size > 0 ? Array.from(gridNames) : null;
    };
    return (
      getGridNames(this.locationPricing?.find(lp => lp.locationId === locationId)) ||
      getGridNames(this.locationPricing?.find(lp => lp.locationId === companyId))
    );
  }

  @memoize
  private getPricingTierForGridName(
    companyId: number,
    locationId: number,
    ptGridName: string
  ): VariantPricingTier | null {
    const enabled = this.shouldUseWeightForPricingTierGridColumn();
    const unitOfMeasure = this.unitOfMeasure;
    const getTier = (price: VariantPricing): VariantPricingTier | null => {
      const tier = price?.pricingTiers?.find(pt => pt?.getGridColumnName(enabled, unitOfMeasure) === ptGridName);
      return tier || null;
    };
    return (
      getTier(this.locationPricing?.find(lp => lp.locationId === locationId)) ||
      getTier(this.locationPricing?.find(lp => lp.locationId === companyId))
    );
  }

  @memoize
  private getClassicFlowerPricingTierFromGridName(
    companyId: number,
    locationId: number,
    ptGridName: string
  ): VariantPricingTier | null {
    const enabled = this.shouldUseWeightForPricingTierGridColumn();
    const getTier = (price: VariantPricing): VariantPricingTier | null => {
      const tier = price?.pricingTiers?.find(pt => pt?.getFlowerGridOunceColumnNames(enabled)?.contains(ptGridName));
      return tier || null;
    };
    return (
      getTier(this.locationPricing?.find(lp => lp.locationId === locationId)) ||
      getTier(this.locationPricing?.find(lp => lp.locationId === companyId))
    );
  }

  // This functionality matches that on the API and must remain consistent for grid column validation logic
  @memoize
  shouldUseWeightForPricingTierGridColumn(): boolean {
    return VariantTypeUtils.isFlowerByGramType(this.variantType);
  }

  @memoize
  private getGridName(): string {
    // If variant is non-pre-roll flower
    const isFlowerByGram = VariantTypeUtils.isFlowerByGramType(this.variantType);
    // If variant is any of the concentrate types
    const isConcentrate = this.productType === ProductType.Concentrate;
    // If variant is any of the liquid oil types (non-capsule)
    const isLiquidOil = this.productType === ProductType.Oil && !VariantTypeUtils.isCapsuleType(this.variantType);
    // If variant is any Vape option
    const isVape = this.productType === ProductType.Vape;
    const isCapsule = VariantTypeUtils.isCapsuleType(this.variantType);
    switch (true) {
      case isFlowerByGram:
        return this.unitSize > 0 ? `${this.unitSize} g` : null;
      case isLiquidOil:
      case isConcentrate:
      case isVape:
        return this.unitSize > 0 ? `${this.unitSize} ${this.unitOfMeasure}` : null;
      case isCapsule:
        return this.packagedQuantity > 0 ? `${this.packagedQuantity} caps` : null;
      default:
        return this.packagedQuantity > 0 ? `${this.packagedQuantity} pk` : null;
    }
  }

  getGridNameAsColumnComparisonString(): string {
    return StringUtils.gridColumnComparisonString(this.getGridName());
  }

  getVariantTitle(): string {
    return this.displayAttributes?.getDisplayName() || this.name;
  }

  getFormattedUnitSize(includeSpacing: boolean = false): string {
    if (this.unitOfMeasure === UnitOfMeasure.NA) {
      return '-';
    } else {
      const size = includeSpacing ? this.unitSize + ' ' + this.unitOfMeasure : this.unitSize + this.unitOfMeasure;
      return size?.trim();
    }
  }

  hasGradingResultsFor(gradingProvider: string): boolean {
    return this.gradingResults?.get(gradingProvider)?.grade > 0 || false;
  }

  getGradingResultGradeFor(gradingProvider: string): number | null {
    return this.gradingResults?.get(gradingProvider)?.grade ?? null;
  }

  @memoize
  getPackageAndSize(): string {
    const pkgQty = this.packagedQuantity;
    const weightValue = this.unitSize;
    const weightUnit = this.unitOfMeasure;
    if (pkgQty > 1) {
      return pkgQty + ' Pack, ' + weightValue + weightUnit;
    } else {
      return weightValue + weightUnit;
    }
  }

  hasBadges(badgeMap: Map<string, VariantBadge[]>): boolean {
    const badges: VariantBadge[] = [];
    badgeMap?.get(this.id)?.forEach(badge => badges.push(badge));
    return badges.length > 0 || this.displayAttributes?.getBadges().length > 0;
  }

  getDisplayAttributes(): DisplayAttribute[] {
    return [this.displayAttributes, this.displayAttributes?.inheritedDisplayAttribute]?.filterNulls();
  }

  hasCraftDesignation(): boolean {
    return this.craftDesignations?.length > 0;
  }

  getEnergyScoreAsNumber(): number | null {
    if (exists(this.energyScore)) {
      const energyScoreAsString = RegexUtils.energyScoreRegex.exec(this.energyScore)?.[1] ?? null;
      return exists(energyScoreAsString) ? Number.parseInt(energyScoreAsString, 10) : null;
    }
    return null;
  }

  public getAllProductTagIds(): string[] {
    const tagIdsSet: Set<string> = new Set<string>();
    this.tagIds?.forEach(tagId => tagIdsSet.add(tagId));
    this.posTagIds?.forEach(posTagId => tagIdsSet.add(posTagId));
    return Array.from(tagIdsSet);
  }

  setUniqueIdentifier(menu: Menu, companyConfig: CompanyConfiguration, locationConfig: LocationConfiguration): string {
    // generate unique id
    const locationPricingIds: string[] = [];
    this.locationPricing?.forEach(p => {
      locationPricingIds.push(p.getUniqueIdentifier());
    });
    const locationPricingId = locationPricingIds.sort().join(',');
    // not including terpenes or descriptions as it is rich text and needs to be reduced
    this.uniqueIdentifier = `
      -${this.companyId}
      -${this.id}
      -${this.name}
      -${this.price}
      -${this.brand}
      -${this.lineages?.join(',')}
      -${this.manufacturer}
      -${this.shortDescription}
      -${this.classification}
      -${this.unitSize}
      -${this.unitOfMeasure}
      -${this.cannabisUnitOfMeasure}
      -${this.terpeneUnitOfMeasure}
      -${this.useCannabinoidRange}
      -${this.useTerpeneRange}
      -${this.packagedQuantity}
      -${this.productType}
      -${this.variantType}
      -${this.strain}
      -${this.THC}
      -${this.CBD}
      -${companyConfig?.getUniqueIdentifier()}
      -${this.inventory?.getUniqueIdentifier()}
      -${locationPricingId}
      -${this.getVisiblePrice(menu.theme, menu.locationId, menu.companyId, locationConfig?.priceFormat, true)}
      -${this.displayAttributes?.getUniqueIdentifier()}
      -${this.locationPromotion?.getUniqueIdentifier()}
      -${this.isDiscontinued}
      -${this.energyScore}
      -${this.tagIds?.sort()?.join(',')}
      -${this.posTagIds?.sort()?.join(',')}
      -${this.custom1}
      -${this.custom2}
      -${this.custom3}
      -${this.flavorGroup}
      -${this.helpsWith?.sort()?.join(',')}
      -${this.effects?.sort()?.join(',')}
      -${this.aromas?.sort()?.join(',')}
      -${this.flavors?.sort()?.join(',')}
      -${this.appearances?.sort()?.join(',')}
      -${this.growingConditions?.sort()?.join(',')}
    `;
    return this.uniqueIdentifier;
  }

  getUniqueIdentifier(): string {
    return this.uniqueIdentifier;
  }
}
